import React, { useEffect, useState } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { brandLogo } from "../../../assets/img/logo/brandLogos";
import {
  FaLock,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaArrowRight
} from "react-icons/fa";

export default function AdminLogin() {
  const history = useHistory();
  const location = useLocation();
  const [loginInput, setLoginInput] = useState({
    email: "",
    password: "",
  });
  const [errorList, setErrorList] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Sign in — Store Administration";
  }, []);

  const handleInput = (e) => {
    e.persist();
    setLoginInput({ ...loginInput, [e.target.name]: e.target.value });
  };

  const loginSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorList({});

    const data = {
      email: loginInput.email,
      password: loginInput.password,
    };

    axios.get("/sanctum/csrf-cookie").then(() => {
      axios
        .post("/api/login", data)
        .then((res) => {
          setLoading(false);
          if (res.data.status === 200) {
            sessionStorage.setItem("auth_token", res.data.token);
            sessionStorage.setItem("auth_name", res.data.username);
            sessionStorage.setItem("role", res.data.role);
            sessionStorage.setItem("id", res.data.id);
            swal("Welcome Back!", res.data.message || "Admin access verified.", "success", {
              buttons: false,
              timer: 1200,
            });

            const requested = location.state?.from;
            const requestedPath = requested
              ? `${requested.pathname || ""}${requested.search || ""}${requested.hash || ""}`
              : "";
            const destination = requestedPath.startsWith("/admin/") && requestedPath !== "/admin/login"
              ? requestedPath
              : "/admin/dashboard";
            history.replace(destination);
          } else if (res.data.status === 401) {
            swal("Access Denied", res.data.message || "Invalid Admin Credentials", "error");
          } else {
            setErrorList(res.data.validation_errors || {});
          }
        })
        .catch((err) => {
          setLoading(false);
          swal("Authentication Error", "Unable to connect to login service. Please try again.", "error");
        });
    });
  };

  return (
    <div className="admin-login">
      <style>{`
        .admin-login {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 28px 16px;
          background:
            radial-gradient(900px 420px at 12% -10%, rgba(37, 99, 235, 0.28), transparent 55%),
            linear-gradient(160deg, #0b1220 0%, #111827 48%, #0f172a 100%);
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }
        .admin-login-card {
          width: 100%;
          max-width: 920px;
          display: grid;
          grid-template-columns: 0.92fr 1.08fr;
          background: #ffffff;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 30px 70px -24px rgba(0, 0, 0, 0.55);
        }
        .admin-login-aside {
          background: #0f172a;
          color: #e2e8f0;
          padding: 48px 40px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: 32px;
        }
        .admin-login-logo {
          background: #ffffff;
          border-radius: 16px;
          padding: 14px 16px;
          width: 100%;
        }
        .admin-login-logo img {
          display: block;
          width: 100%;
          height: auto;
          max-height: 120px;
          object-fit: contain;
        }
        .admin-login-kicker {
          margin: 28px 0 8px;
          font-size: 12px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #93c5fd;
          font-weight: 700;
        }
        .admin-login-aside h1 {
          margin: 0 0 10px;
          font-size: 30px;
          line-height: 1.15;
          letter-spacing: -0.04em;
          color: #ffffff;
          font-weight: 700;
        }
        .admin-login-aside p {
          margin: 0;
          color: #94a3b8;
          font-size: 14.5px;
          line-height: 1.6;
          max-width: 280px;
        }
        .admin-login-note {
          font-size: 12.5px;
          color: #64748b;
        }
        .admin-login-form {
          padding: 48px 44px;
        }
        .admin-login-form h2 {
          margin: 0 0 6px;
          font-size: 26px;
          letter-spacing: -0.03em;
          color: #0f172a;
          font-weight: 700;
        }
        .admin-login-form .lede {
          margin: 0 0 28px;
          color: #64748b;
          font-size: 14px;
        }
        .admin-login-label {
          display: block;
          margin-bottom: 8px;
          font-size: 13px;
          font-weight: 600;
          color: #334155;
        }
        .admin-login-field {
          position: relative;
          margin-bottom: 18px;
        }
        .admin-login-field > svg {
          position: absolute;
          left: 14px;
          top: 16px;
          color: #94a3b8;
          pointer-events: none;
        }
        .admin-login-field input {
          width: 100%;
          height: 48px;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 0 44px 0 40px;
          font-size: 14.5px;
          color: #0f172a;
          background: #f8fafc;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }
        .admin-login-field input:focus {
          outline: none;
          background: #ffffff;
          border-color: #2563eb;
          box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
        }
        .admin-login-field input.is-invalid {
          border-color: #ef4444;
        }
        .admin-login-eye {
          position: absolute;
          right: 6px;
          top: 6px;
          width: 36px;
          height: 36px;
          border: 0;
          border-radius: 10px;
          background: transparent;
          color: #64748b;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .admin-login-eye:hover {
          background: #e2e8f0;
          color: #0f172a;
        }
        .admin-login-error {
          display: block;
          margin-top: -10px;
          margin-bottom: 14px;
          color: #b91c1c;
          font-size: 12.5px;
        }
        .admin-login-submit {
          width: 100%;
          height: 48px;
          margin-top: 6px;
          border: 0;
          border-radius: 12px;
          background: #0f172a;
          color: #ffffff;
          font-size: 14.5px;
          font-weight: 650;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .admin-login-submit:hover:not(:disabled) {
          background: #1e293b;
        }
        .admin-login-submit:disabled {
          opacity: 0.7;
        }
        .admin-login-back {
          display: inline-block;
          margin-top: 22px;
          color: #64748b;
          font-size: 13px;
          text-decoration: none;
        }
        .admin-login-back:hover {
          color: #0f172a;
        }
        @media (max-width: 800px) {
          .admin-login-card {
            grid-template-columns: 1fr;
            max-width: 440px;
          }
          .admin-login-aside {
            padding: 28px 24px 8px;
            gap: 0;
          }
          .admin-login-aside h1,
          .admin-login-aside p,
          .admin-login-note {
            display: none;
          }
          .admin-login-kicker {
            margin-top: 16px;
            margin-bottom: 0;
          }
          .admin-login-form {
            padding: 28px 24px 32px;
          }
        }
      `}</style>

      <div className="admin-login-card">
        <aside className="admin-login-aside">
          <div>
            <div className="admin-login-logo">
              <img
                src={brandLogo}
                alt="GetGrowCart"
              />
            </div>
            <div className="admin-login-kicker">GetGrowCart</div>
            <h1>Store administration</h1>
            <p>Catalog, orders, and customers, kept in one quiet place.</p>
          </div>
          <div className="admin-login-note">Staff access only</div>
        </aside>

        <div className="admin-login-form">
          <h2>Sign in</h2>
          <p className="lede">Use the admin email and password for this store.</p>

          <form onSubmit={loginSubmit}>
            <label className="admin-login-label" htmlFor="admin-email">
              Email
            </label>
            <div className="admin-login-field">
              <FaEnvelope size={14} />
              <input
                id="admin-email"
                type="email"
                name="email"
                onChange={handleInput}
                value={loginInput.email}
                className={errorList.email ? "is-invalid" : ""}
                placeholder="admin@store.com"
                autoComplete="username"
                required
              />
            </div>
            {errorList.email && <small className="admin-login-error">{errorList.email[0]}</small>}

            <label className="admin-login-label" htmlFor="admin-password">
              Password
            </label>
            <div className="admin-login-field">
              <FaLock size={14} />
              <input
                id="admin-password"
                type={showPassword ? "text" : "password"}
                name="password"
                onChange={handleInput}
                value={loginInput.password}
                className={errorList.password ? "is-invalid" : ""}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="admin-login-eye"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
              </button>
            </div>
            {errorList.password && <small className="admin-login-error">{errorList.password[0]}</small>}

            <button type="submit" className="admin-login-submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status"></span>
                  <span>Signing in</span>
                </>
              ) : (
                <>
                  <span>Sign in</span>
                  <FaArrowRight size={12} />
                </>
              )}
            </button>
          </form>

          <Link to="/" className="admin-login-back">
            Return to the store
          </Link>
        </div>
      </div>
    </div>
  );
}
